import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const API_URL = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin) + '/api';
const SHEET_LABEL = { ffe: 'FF&E', checklist: 'Checklist', walkthrough: 'Walkthrough' };

/**
 * Houzz import + finish + vendor toolbar, embedded on each sheet page. The
 * import always targets the sheet it sits on (FF&E import also loads Checklist,
 * per the app rule). Self-contained: renders its own preview & fix-finish modals.
 */
export const HouzzToolbar = ({ projectId, projectName, sheetType = 'ffe', onImported }) => {
  const target = sheetType;
  const label = SHEET_LABEL[target] || 'FF&E';
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null); // {file, data}
  const [upgradeCover, setUpgradeCover] = useState(true);
  const [missing, setMissing] = useState(null); // {items}
  const [missingBusy, setMissingBusy] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [combine, setCombine] = useState(null); // {ffeFile, tearFiles:[]}
  const [combineBusy, setCombineBusy] = useState(false);
  const [combineProgress, setCombineProgress] = useState(null); // {pct, stage}
  const [report, setReport] = useState(null); // {title, ffe, finish} — post-merge Unmatched Report
  const [confirmedMatches, setConfirmedMatches] = useState({}); // sku -> 'done' | 'busy' | 'skip'
  const [replaceExisting, setReplaceExisting] = useState(false); // clean import: wipe sheet items first
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [undoableBatchId, setUndoableBatchId] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [rulesBusy, setRulesBusy] = useState(false);
  const [newRule, setNewRule] = useState({ rule_type: 'keyword', pattern: '', category: '', subcategory: '' });
  const [refileOpen, setRefileOpen] = useState(false);
  const [refilePreview, setRefilePreview] = useState(null);
  const [refileResult, setRefileResult] = useState(null);
  const [refileBusy, setRefileBusy] = useState(false);
  const [refileProgress, setRefileProgress] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [resetSheetsBusy, setResetSheetsBusy] = useState(false);
  const [mergeRoomsBusy, setMergeRoomsBusy] = useState(false);
  const [upgradeImagesBusy, setUpgradeImagesBusy] = useState(false);
  const [upgradeProgress, setUpgradeProgress] = useState('');

  const loadHistory = useCallback(async () => {
    setHistoryBusy(true);
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/import-history`);
      const data = await res.json();
      setHistory(data.history || []);
      setUndoableBatchId(data.undoable_batch_id || null);
    } catch (e) { setHistory([]); }
    finally { setHistoryBusy(false); }
  }, [projectId]);

  const openHistory = () => { setHistoryOpen(true); loadHistory(); };

  // One-click confirm of an auto-suggested tear swatch -> FF&E item (by SKU).
  const confirmMatch = async (sug) => {
    const s = sug.suggestion;
    if (!s) return;
    setConfirmedMatches((m) => ({ ...m, [sug.sku]: 'busy' }));
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/confirm-finish-match`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: s.item_id, finish_image: sug.swatch_url || null, cover_image: sug.cover_url || null }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Could not apply match');
      setConfirmedMatches((m) => ({ ...m, [sug.sku]: 'done' }));
      if (onImported) onImported();
    } catch (err) {
      setConfirmedMatches((m) => ({ ...m, [sug.sku]: undefined }));
      window.alert('Match failed: ' + (err.message || err));
    }
  };

  const refreshLastImport = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/last-import`);
      const data = await res.json();
      setLastImport(data.has_undo ? data.batch : null);
    } catch (e) { /* non-critical */ }
  }, [projectId]);

  useEffect(() => { refreshLastImport(); }, [refreshLastImport]);

  const previewImport = async (e) => {
    const fileEl = e.target; const file = fileEl.files && fileEl.files[0]; if (!file) return;
    setBusy('preview');
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('target', target);
      const res = await fetch(`${API_URL}/projects/${projectId}/import-houzz-pdf/preview`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Preview failed');
      setPreview({ file, data }); setReplaceExisting(false);
    } catch (err) { window.alert('Preview failed: ' + (err.message || err)); }
    finally { setBusy(null); fileEl.value = ''; }
  };

  const confirmImport = async () => {
    if (!preview) return;
    if (replaceExisting && !window.confirm(`CLEAN IMPORT — replaces every row this PDF contains with a fresh copy${target === 'ffe' ? ' (FF&E and Checklist)' : ''} in "${projectName}", clearing stale/wrong rows left by earlier imports.\n\nAnything you added by hand that is NOT in this PDF (e.g. paint/finishes) is KEPT.\n\nReversible with "Undo last import".\n\nContinue?`)) return;
    setBusy('commit');
    setCombineProgress({ pct: 0, stage: 'Uploading your PDF…' });
    try {
      // Run the import as a BACKGROUND job (same proven path as the combined
      // import) and poll until it finishes. A large schedule (hundreds of
      // products + photos) takes far longer than the gateway request timeout,
      // so a synchronous call returned a false 502 even though the server
      // finished. The job returns a job_id instantly, then we poll its status.
      const fd = new FormData();
      fd.append('ffe_file', preview.file);
      fd.append('target', target);
      fd.append('replace_existing', replaceExisting ? 'true' : 'false');
      const res = await fetch(`${API_URL}/projects/${projectId}/import-houzz-combined`, { method: 'POST', body: fd });
      let start;
      try { start = await res.json(); } catch { throw new Error('Upload did not go through — please try again.'); }
      if (!res.ok || !start.job_id) throw new Error(start.detail || 'Could not start the import.');

      const jobId = start.job_id;
      setCombineProgress({ pct: 3, stage: 'Starting import…' });
      const deadline = Date.now() + 15 * 60 * 1000; // 15 min safety cap
      let result = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        let job;
        try {
          const jr = await fetch(`${API_URL}/projects/${projectId}/import-jobs/${jobId}`);
          job = await jr.json();
        } catch { continue; } // transient network blip — keep polling
        if (job && job.progress) setCombineProgress(job.progress);
        if (job.status === 'done') { result = job.result || {}; break; }
        if (job.status === 'error') { throw new Error(job.error || 'Import failed'); }
      }
      if (!result) throw new Error('The import is taking longer than expected. It may still be finishing — refresh in a minute to check.');

      const data = result.ffe || {};
      setPreview(null);
      const roomCount = (data.rooms_named && data.rooms_named.length) || data.rooms || 0;
      const extra = target === 'ffe' ? ' and mirrored to the Checklist' : '';
      const cleaned = [];
      if (data.duplicates_removed) cleaned.push(`${data.duplicates_removed} duplicate(s) removed`);
      if (data.rooms_merged) cleaned.push(`${data.rooms_merged} duplicate room(s) combined`);
      const cleanedTxt = cleaned.length ? `\nCleaned up: ${cleaned.join(', ')}.` : '';
      window.alert(`Imported ${data.products} products into ${label}${extra} — ${roomCount} rooms, ${data.photos} photos.${cleanedTxt}`);
      refreshLastImport();
      if (onImported) onImported();
    } catch (err) { window.alert('Import failed: ' + (err.message || err)); }
    finally { setBusy(null); setCombineProgress(null); }
  };

  const finishImport = async (e) => {
    const fileEl = e.target; const file = fileEl.files && fileEl.files[0]; if (!file) return;
    setBusy('finish');
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('upgrade_cover', upgradeCover ? 'true' : 'false');
      const res = await fetch(`${API_URL}/projects/${projectId}/import-finish-photos`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Finish import failed');
      setConfirmedMatches({});
      setReport({ title: 'Tear sheet merge complete', ffe: null, finish: { tear_files: 1, tear_products: data.tear_products, matched_skus: data.matched_skus, items_updated: data.items_updated, unmatched_skus: data.unmatched_skus || [], unmatched_suggestions: data.unmatched_suggestions || [] } });
      if (onImported) onImported();
    } catch (err) { window.alert('Finish import failed: ' + (err.message || err)); }
    finally { setBusy(null); fileEl.value = ''; }
  };

  const openMissing = async () => {
    setMissingBusy(true);
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/missing-finish-items?sheet_type=${target}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load');
      setMissing({ items: data.items || [] });
    } catch (err) { window.alert('Could not load missing-finish items: ' + (err.message || err)); }
    finally { setMissingBusy(false); }
  };

  const uploadFinishForItem = async (itemId, e) => {
    const fileEl = e.target; const file = fileEl.files && fileEl.files[0]; if (!file) return;
    setMissingBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('item_id', itemId); fd.append('image_type', 'finish');
      const res = await fetch(`${API_URL}/upload-item-image`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || data.message || 'Upload failed');
      setMissing((prev) => (prev ? { ...prev, items: (prev.items || []).filter((x) => x.id !== itemId) } : prev));
      if (onImported) onImported();
    } catch (err) { window.alert('Upload failed: ' + (err.message || err)); }
    finally { setMissingBusy(false); fileEl.value = ''; }
  };

  const autoMatchFinishes = async (e) => {
    const fileEl = e.target; const file = fileEl.files && fileEl.files[0]; if (!file) return;
    setMissingBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('upgrade_cover', 'false');
      const res = await fetch(`${API_URL}/projects/${projectId}/import-finish-photos`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Auto-match failed');
      const mres = await fetch(`${API_URL}/projects/${projectId}/missing-finish-items?sheet_type=${target}`);
      const mdata = await mres.json();
      setMissing((prev) => (prev ? { ...prev, items: mdata.items || [] } : prev));
      window.alert(`Auto-matched ${data.items_updated} item(s) from the tear sheet. ${(data.unmatched_skus || []).length} SKU(s) had no match — pick those by hand below.`);
      if (onImported) onImported();
    } catch (err) { window.alert('Auto-match failed: ' + (err.message || err)); }
    finally { setMissingBusy(false); fileEl.value = ''; }
  };

  const openVendorSheet = () => window.open(`${API_URL}/projects/${projectId}/vendor-order-sheet/print?sheet_type=${target}`, '_blank', 'noopener');

  const runCombined = async () => {
    if (!combine || !combine.ffeFile) { window.alert('Please choose the FF&E Schedule PDF first.'); return; }
    if (replaceExisting && !window.confirm(`CLEAN IMPORT — this first REMOVES all current ${label} items${target === 'ffe' ? ' (and the Checklist)' : ''} in "${projectName}", then imports fresh so no stale/wrong rows from earlier imports remain.\n\nYou can reverse it with "Undo last import".\n\nContinue?`)) return;
    setCombineBusy(true);
    setCombineProgress({ pct: 0, stage: 'Uploading your PDFs…' });
    try {
      const fd = new FormData();
      fd.append('ffe_file', combine.ffeFile);
      (combine.tearFiles || []).forEach((f) => fd.append('tear_files', f));
      fd.append('target', target);
      fd.append('upgrade_cover', upgradeCover ? 'true' : 'false');
      fd.append('replace_existing', replaceExisting ? 'true' : 'false');
      const res = await fetch(`${API_URL}/projects/${projectId}/import-houzz-combined`, { method: 'POST', body: fd });
      let start;
      try { start = await res.json(); } catch { throw new Error('Upload did not go through — please try again.'); }
      if (!res.ok || !start.job_id) throw new Error(start.detail || 'Could not start the import.');

      // The import runs in the background; poll until it finishes (handles big
      // PDFs / long imports without the gateway timing the request out).
      const jobId = start.job_id;
      setCombineProgress({ pct: 3, stage: 'Starting import…' });
      const deadline = Date.now() + 15 * 60 * 1000; // 15 min safety cap
      let data = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        let job;
        try {
          const jr = await fetch(`${API_URL}/projects/${projectId}/import-jobs/${jobId}`);
          job = await jr.json();
        } catch { continue; } // transient network blip — keep polling
        if (job && job.progress) setCombineProgress(job.progress);
        if (job.status === 'done') { data = job.result || {}; break; }
        if (job.status === 'error') { throw new Error(job.error || 'Import failed'); }
      }
      if (!data) throw new Error('The import is taking longer than expected. It may still be finishing — refresh in a minute to check.');

      const f = data.ffe || {}, fin = data.finish || {};
      setConfirmedMatches({});
      setReport({ title: 'Import & merge complete', ffe: f, finish: fin });
      setCombine(null);
      refreshLastImport();
      if (onImported) onImported();
    } catch (err) { window.alert('Import & merge failed: ' + (err.message || err)); }
    finally { setCombineBusy(false); setCombineProgress(null); }
  };

  const fixNames = async () => {
    setBusy('fixnames');
    try {
      const dry = await (await fetch(`${API_URL}/projects/${projectId}/fix-imported-names?sheet_type=${target}&dry_run=true`, { method: 'POST' })).json();
      if (!dry.candidates) { window.alert(`No blank or "Imported item" names to fix on ${label}.`); return; }
      const eg = (dry.examples || []).slice(0, 8).map((e) => `• ${e.old} → ${e.new}`).join('\n');
      if (!window.confirm(`Found ${dry.candidates} item(s) with a blank or "Imported item" name. Give them names derived from their description / vendor / SKU?\n\n${eg}`)) return;
      const res = await fetch(`${API_URL}/projects/${projectId}/fix-imported-names?sheet_type=${target}&dry_run=false`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fix failed');
      window.alert(`Renamed ${data.renamed} item(s).`);
      if (onImported) onImported();
    } catch (err) { window.alert('Fix names failed: ' + (err.message || err)); }
    finally { setBusy(null); }
  };

  const removeDuplicates = async () => {
    setBusy('dedupe');
    try {
      const dry = await (await fetch(`${API_URL}/projects/${projectId}/dedupe-items?sheet_type=${target}&dry_run=true`, { method: 'POST' })).json();
      if (!dry.items_to_remove) { window.alert(`No duplicate ${label} items found (matching same room + name + SKU).`); return; }
      const eg = (dry.examples || []).slice(0, 8).map((e) => `• ${e.name} (${e.copies}×)`).join('\n');
      if (!window.confirm(`Found ${dry.items_to_remove} duplicate ${label} item(s) across ${dry.duplicate_sets} product(s) — same room, name & SKU. The most complete copy of each is kept.\n\n${eg}\n\nRemove the ${dry.items_to_remove} duplicate(s)?`)) return;
      const res = await fetch(`${API_URL}/projects/${projectId}/dedupe-items?sheet_type=${target}&dry_run=false`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Dedupe failed');
      window.alert(`Removed ${data.removed} duplicate ${label} item(s).`);
      if (onImported) onImported();
    } catch (err) { window.alert('Remove duplicates failed: ' + (err.message || err)); }
    finally { setBusy(null); }
  };

  const undoLastImport = async () => {
    const msg = lastImport
      ? `Undo the last import for this project?\n\nDeletes ${lastImport.created_items} added item(s) and restores ${lastImport.updated_items} refreshed item(s).`
      : 'Undo the last import for this project?';
    if (!window.confirm(msg)) return;
    setBusy('undo');
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/undo-houzz-import`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Undo failed');
      window.alert(`Undone: ${data.deleted_items} item(s) removed, ${data.restored_items} restored, ${data.removed_rooms} empty room(s) cleaned up.`);
      refreshLastImport();
      if (historyOpen) loadHistory();
      if (onImported) onImported();
    } catch (err) { window.alert('Undo failed: ' + (err.message || err)); }
    finally { setBusy(null); }
  };

  const btn = 'text-[11px] px-2.5 py-1 rounded cursor-pointer font-semibold transition-colors';

  const loadRules = useCallback(async () => {
    setRulesBusy(true);
    try {
      const res = await fetch(`${API_URL}/category-mapping-rules`);
      const data = await res.json();
      setRules(data.rules || []);
    } catch (e) { setRules([]); }
    finally { setRulesBusy(false); }
  }, []);

  const openMapRules = () => { setMapOpen(true); loadRules(); };

  const addRule = async () => {
    const pattern = (newRule.pattern || '').trim();
    const category = (newRule.category || '').trim();
    if (!pattern || !category) { window.alert('Enter a vendor/keyword and a category.'); return; }
    setRulesBusy(true);
    try {
      const res = await fetch(`${API_URL}/category-mapping-rules`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRule),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Could not add rule');
      setNewRule({ rule_type: 'keyword', pattern: '', category: '', subcategory: '' });
      await loadRules();
    } catch (err) { window.alert('Add rule failed: ' + (err.message || err)); }
    finally { setRulesBusy(false); }
  };

  const deleteRule = async (id) => {
    setRulesBusy(true);
    try {
      await fetch(`${API_URL}/category-mapping-rules/${id}`, { method: 'DELETE' });
      await loadRules();
    } catch (err) { /* noop */ }
    finally { setRulesBusy(false); }
  };

  const openRefile = async () => {
    setRefileOpen(true); setRefilePreview(null); setRefileResult(null); setRefileProgress(null); setRefileBusy(true);
    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/projects/${projectId}/refile-ffe/status`);
        const d = await r.json();
        if (d.status === 'running') {
          setRefileProgress({ done: d.vision_done || 0, total: d.vision_total || 0, phase: d.phase });
          setTimeout(poll, 1500);
        } else if (d.status === 'done') {
          setRefileProgress(null); setRefilePreview(d); setRefileBusy(false);
        } else if (d.status === 'error') {
          setRefilePreview({ error: d.error || 'Classification failed. Please try again.' }); setRefileBusy(false);
        } else {
          setTimeout(poll, 1500);
        }
      } catch (e) { setTimeout(poll, 2000); }
    };
    try {
      await fetch(`${API_URL}/projects/${projectId}/refile-ffe/start`, { method: 'POST' });
      poll();
    } catch (e) { setRefilePreview({ error: 'Could not start the re-file. Please try again.' }); setRefileBusy(false); }
  };

  const doRefile = async () => {
    setRefileBusy(true);
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/refile-ffe`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Re-file failed');
      setRefileResult(data);
      if (onImported) onImported();
    } catch (err) { window.alert('Re-file failed: ' + (err.message || err)); }
    finally { setRefileBusy(false); }
  };

  const undoRefile = async () => {
    setRefileBusy(true);
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/refile-ffe/undo`, { method: 'POST' });
      const data = await res.json();
      if (onImported) onImported();
      window.alert(`Re-file undone — ${data.restored || 0} items put back where they were.`);
      setRefileOpen(false); setRefileResult(null); setRefilePreview(null);
    } catch (err) { window.alert('Undo failed: ' + (err.message || err)); }
    finally { setRefileBusy(false); }
  };

  const doCleanup = async () => {
    setCleanupBusy(true);
    try {
      const pr = await fetch(`${API_URL}/projects/${projectId}/cleanup-ffe/preview`);
      const p = await pr.json();
      const dupes = p.duplicate_items || 0;
      const rooms = p.empty_rooms || [];
      if (dupes === 0 && rooms.length === 0) {
        window.alert('Nothing to clean up — no duplicate items and no empty rooms. 🎉');
        return;
      }
      const roomList = rooms.length ? `\n\nEmpty rooms to delete:\n• ${rooms.slice(0, 15).join('\n• ')}${rooms.length > 15 ? `\n…and ${rooms.length - 15} more` : ''}` : '';
      const ok = window.confirm(`Clean up this project?\n\n• ${dupes} duplicate item${dupes === 1 ? '' : 's'} will be removed\n• ${rooms.length} empty / placeholder room${rooms.length === 1 ? '' : 's'} will be deleted${roomList}\n\nThis can be undone with the button that appears next.`);
      if (!ok) return;
      const res = await fetch(`${API_URL}/projects/${projectId}/cleanup-ffe`, { method: 'POST' });
      const data = await res.json();
      if (onImported) onImported();
      const undo = window.confirm(`✓ Cleaned up: removed ${data.removed_duplicates || 0} duplicates, ${data.removed_blank_items || 0} blank items, ${data.removed_rooms || 0} rooms, ${data.removed_categories || 0} categories, ${data.removed_subcategories || 0} subcategories.\n\nClick Cancel to keep it, or OK to UNDO.`);
      if (undo) {
        const u = await fetch(`${API_URL}/projects/${projectId}/cleanup-ffe/undo`, { method: 'POST' });
        const ud = await u.json();
        if (onImported) onImported();
        window.alert(`Undone — restored ${ud.restored_items || 0} items and ${ud.restored_rooms || 0} rooms.`);
      }
    } catch (err) { window.alert('Clean up failed: ' + (err.message || err)); }
    finally { setCleanupBusy(false); }
  };

  const doMergeRooms = async () => {
    setMergeRoomsBusy(true);
    try {
      const dr = await fetch(`${API_URL}/projects/${projectId}/duplicate-rooms?sheet_type=ffe`);
      if (!dr.ok) throw new Error(`Server returned ${dr.status}`);
      const { clusters = [] } = await dr.json();
      if (!clusters.length) { window.alert('No duplicate rooms found — nothing to merge.'); return; }
      const lines = clusters.map(c => {
        const names = c.rooms.map(r => `${r.name} (${r.item_count})`).join('  +  ');
        return `\u2022 ${names}`;
      }).join('\n');
      const ok = window.confirm(
        `Merge ${clusters.length} set(s) of duplicate rooms?\n\n${lines}\n\n`
        + 'All items/categories move into the room with the most items (shown first), '
        + 'and the empty copies are removed. Every item keeps its id, so your trade '
        + 'sheets stay connected.');
      if (!ok) return;
      const groups = clusters.map(c => ({
        survivor_id: c.suggested_survivor_id,
        duplicate_ids: c.rooms.map(r => r.id).filter(id => id !== c.suggested_survivor_id),
      }));
      const res = await fetch(`${API_URL}/projects/${projectId}/merge-rooms`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheet_type: 'ffe', groups }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      window.alert(`\u2713 Merged ${data.rooms_deleted || 0} duplicate room(s); combined ${data.items_deduped || 0} duplicate item(s).`);
      if (onImported) onImported();
    } catch (err) { window.alert('Merge duplicate rooms failed: ' + (err.message || err)); }
    finally { setMergeRoomsBusy(false); }
  };

  const doUpgradeImages = async () => {
    setUpgradeImagesBusy(true);
    setUpgradeProgress('starting…');
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/upgrade-images`, { method: 'POST' });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const { job_id } = await res.json();
      let last = null;
      for (let i = 0; i < 300; i++) {
        await new Promise(r => setTimeout(r, 2500));
        const st = await fetch(`${API_URL}/projects/${projectId}/upgrade-images/status/${job_id}`);
        if (!st.ok) continue;
        last = await st.json();
        if (last.phase === 'vendor' && last.total) setUpgradeProgress(`${last.done || 0}/${last.total}`);
        else setUpgradeProgress('scanning…');
        if (last.status === 'done' || last.status === 'error') break;
      }
      if (last && last.status === 'error') {
        window.alert('Image upgrade hit an error: ' + (last.error || 'unknown'));
      } else if (last) {
        window.alert(`\u2713 Image upgrade complete.\n\u2022 ${last.tear_covers || 0} photo(s) upgraded from your tear sheets\n\u2022 ${last.vendor_upgraded || 0} low-res photo(s) replaced with the vendor's own hi-res image\n\nReloading to show the sharp photos.`);
        if (onImported) onImported();
      } else {
        window.alert('Image upgrade is still running in the background — give it a minute, then refresh.');
      }
    } catch (err) { window.alert('Image upgrade failed: ' + (err.message || err)); }
    finally { setUpgradeImagesBusy(false); setUpgradeProgress(''); }
  };



  const doResetSheets = async () => {
    const ok = window.confirm('Reset ALL trade sheets to match the FF\u0026E?\n\nEach trade sheet (Electrician, Plumber, Tile, Wallpaper, Painter\u2026) is just a filtered VIEW of the FF\u0026E. This clears any sheet-only include/exclude ticks and field edits, so every sheet shows exactly what the FF\u0026E has for that trade. Your FF\u0026E is NOT changed.\n\n(You can undo an individual sheet from its own "Undo reset" banner.)');
    if (!ok) return;
    setResetSheetsBusy(true);
    try {
      const res = await fetch(`${API_URL}/export-sheets/${projectId}/reset-all`, { method: 'POST' });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      window.alert(`\u2713 Reset ${data.sheets_reset || 0} trade sheet(s) to the FF\u0026E.`);
      if (onImported) onImported();
    } catch (err) { window.alert('Reset failed: ' + (err.message || err)); }
    finally { setResetSheetsBusy(false); }
  };

  return (
    <div className="mb-4 p-3 rounded-xl border border-[#8b7355]/40 flex flex-wrap items-center gap-2"
      style={{ background: 'linear-gradient(135deg, rgba(20,20,30,0.9), rgba(15,15,25,0.9))' }}
      data-testid={`houzz-toolbar-${target}`}>
      <span className="text-[11px] text-[#D4A574] font-bold uppercase tracking-wide mr-1">🏠 Houzz → {label}</span>

      <button onClick={() => { setCombine({ ffeFile: null, tearFiles: [] }); setReplaceExisting(false); }} data-testid={`toolbar-combined-${target}`}
        title={`Upload the ${label} Schedule PDF AND its tear sheets together — imports and merges in one shot, pulling each finish swatch into the finish picture by SKU`}
        className={`${btn} bg-[#D4A574] text-black hover:bg-[#e2c08a] shadow`}>
        ⬆ Import {label} + Tear Sheets
      </button>

      <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-import-${target}`}
        title={`Import ONLY a ${label} Schedule PDF (preview before saving, no tear-sheet merge)`}
        className={`${btn} ${busy === 'preview' ? 'bg-[#D4A574]/40 text-stone-400' : 'bg-[#D4A574]/25 text-[#e2c08a] hover:bg-[#D4A574]/45'}`}>
        {busy === 'preview' ? '⏳ Reading…' : `⬆ ${label} only`}
        <input type="file" accept="application/pdf,.pdf" data-testid={`toolbar-import-input-${target}`} disabled={!!busy} onChange={previewImport} className="hidden" />
      </label>

      <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-finish-${target}`}
        title="Merge a Houzz tear-sheet PDF into existing items by SKU — attaches each product's finish/color swatch (never the main photo)"
        className={`${btn} ${busy === 'finish' ? 'bg-emerald-700/40 text-stone-400' : 'bg-emerald-700/30 text-emerald-200 hover:bg-emerald-700/50'}`}>
        {busy === 'finish' ? '⏳…' : '🎨 Tear Sheets only'}
        <input type="file" accept="application/pdf,.pdf" data-testid={`toolbar-finish-input-${target}`} disabled={!!busy} onChange={finishImport} className="hidden" />
      </label>

      <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-hires-${target}`}
        title="Also replace each product's cover with the tear sheet's hi-res main photo"
        className="text-[10px] text-stone-400 flex items-center gap-1 cursor-pointer">
        <input type="checkbox" checked={upgradeCover} data-testid={`toolbar-hires-input-${target}`} onChange={(e) => setUpgradeCover(e.target.checked)} className="w-3 h-3" />
        hi-res cover
      </label>

      <button onClick={openMissing} disabled={missingBusy} data-testid={`toolbar-fix-finishes-${target}`}
        title={`List every ${label} item still missing a finish swatch and attach one by hand`}
        className={`${btn} bg-rose-700/30 text-rose-200 hover:bg-rose-700/50 disabled:opacity-50`}>
        {missingBusy && !missing ? '⏳…' : '🩹 Fix Finishes'}
      </button>

      <button onClick={openVendorSheet} data-testid={`toolbar-vendor-sheet-${target}`}
        title={`Printable purchasing sheet of every ${label} item that has a product link, grouped by vendor`}
        className={`${btn} bg-[#2B4C7E]/40 text-sky-200 hover:bg-[#2B4C7E]/60`}>
        🧾 Vendor Order Sheet
      </button>

      <button onClick={removeDuplicates} disabled={busy === 'dedupe'} data-testid={`toolbar-dedupe-${target}`}
        title={`Find and remove duplicate ${label} line items (same room + name + SKU). Shows the count first; keeps the most complete copy of each.`}
        className={`${btn} bg-orange-700/30 text-orange-200 hover:bg-orange-700/50 disabled:opacity-50`}>
        {busy === 'dedupe' ? '⏳…' : '🧹 Remove Duplicates'}
      </button>

      <button onClick={fixNames} disabled={busy === 'fixnames'} data-testid={`toolbar-fixnames-${target}`}
        title={`Give a real name to items left as "Imported item"/blank, derived from their description, vendor or SKU. Shows the count first.`}
        className={`${btn} bg-teal-700/30 text-teal-200 hover:bg-teal-700/50 disabled:opacity-50`}>
        {busy === 'fixnames' ? '⏳…' : '🏷 Fix Blank Names'}
      </button>

      {lastImport && (
        <button onClick={undoLastImport} disabled={busy === 'undo'} data-testid={`toolbar-undo-${target}`}
          title={`Roll back the last import (${lastImport.created_items} added, ${lastImport.updated_items} refreshed)`}
          className={`${btn} bg-amber-800/40 text-amber-200 hover:bg-amber-800/60 disabled:opacity-50`}>
          {busy === 'undo' ? '⏳…' : `↩ Undo last import (${lastImport.created_items + lastImport.updated_items})`}
        </button>
      )}

      <button onClick={openHistory} data-testid={`toolbar-history-${target}`}
        title="See every import for this project, newest first — and revert the latest."
        className={`${btn} bg-stone-700/40 text-stone-200 hover:bg-stone-700/60`}>
        🕑 Import history
      </button>

      <button onClick={openMapRules} data-testid={`toolbar-maprules-${target}`}
        title="Create rules that map a vendor or keyword to a category / subcategory, so every import files those products exactly where you want."
        className={`${btn} bg-indigo-700/30 text-indigo-200 hover:bg-indigo-700/50`}>
        ⚙ Mapping Rules
      </button>

      <button onClick={openRefile} data-testid={`toolbar-refile-${target}`}
        title="Sort every item into the correct category/subcategory by its name & vendor. Shows a preview first, keeps every item's id so your sheets stay intact, deletes nothing, and can be undone in one click."
        className={`${btn} bg-teal-700/30 text-teal-200 hover:bg-teal-700/50`}>
        🗂 Re-file Categories
      </button>

      <button onClick={doCleanup} disabled={cleanupBusy} data-testid={`toolbar-cleanup-${target}`}
        title="Remove duplicate items and delete empty / placeholder rooms, categories and subcategories. Shows a count first and can be undone in one click."
        className={`${btn} bg-rose-700/30 text-rose-200 hover:bg-rose-700/50 disabled:opacity-50`}>
        {cleanupBusy ? '⏳ Cleaning…' : '🧹 Clean Up'}
      </button>

      {target === 'ffe' && (
        <button onClick={doUpgradeImages} disabled={upgradeImagesBusy} data-testid={`toolbar-upgrade-images-${target}`}
          title="Replace low-res thumbnails with the HIGH-RES product photos from the tear sheets you already imported (no re-upload). These flow to every trade sheet."
          className={`${btn} bg-emerald-700/30 text-emerald-200 hover:bg-emerald-700/50 disabled:opacity-50`}>
          {upgradeImagesBusy ? `⏳ Upgrading… ${upgradeProgress}` : '🖼️ Upgrade Image Quality'}
        </button>
      )}

      {target === 'ffe' && (
        <button onClick={doMergeRooms} disabled={mergeRoomsBusy} data-testid={`toolbar-merge-rooms-${target}`}
          title="Find rooms that are the same room listed twice (e.g. 'Kate & Dawson' + 'Kate and Dawson', or Primary Bedroom on two floors) and merge them into one. Every item keeps its id, so trade sheets stay connected."
          className={`${btn} bg-fuchsia-700/30 text-fuchsia-200 hover:bg-fuchsia-700/50 disabled:opacity-50`}>
          {mergeRoomsBusy ? '⏳ Merging…' : '🔗 Merge Duplicate Rooms'}
        </button>
      )}

      {target === 'ffe' && (
        <button onClick={doResetSheets} disabled={resetSheetsBusy} data-testid={`toolbar-reset-sheets-${target}`}
          title="Reset EVERY trade sheet (Electrician, Plumber, Tile, Wallpaper, Painter…) so each shows exactly what the FF&E has for that trade. Clears sheet-only tweaks only; your FF&E is untouched."
          className={`${btn} bg-amber-700/30 text-amber-200 hover:bg-amber-700/50 disabled:opacity-50`}>
          {resetSheetsBusy ? '⏳ Resetting…' : '↺ Reset Trade Sheets'}
        </button>
      )}

      {mapOpen && createPortal((
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100001] p-4"
          data-testid={`toolbar-maprules-modal-${target}`} onClick={() => setMapOpen(false)}>
          <div className="bg-[#1a1a1a] border border-[#8b7355] rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#8b7355]/40 flex items-center justify-between">
              <div className="text-[#D4A574] font-bold text-base">⚙ Custom Mapping Rules</div>
              <button onClick={() => setMapOpen(false)} data-testid={`toolbar-maprules-close-${target}`}
                className="text-stone-400 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="px-5 py-3 border-b border-[#8b7355]/30 text-stone-400 text-xs">
              Rules run on every import. A product matching a rule is filed into the chosen category / subcategory
              <span className="text-stone-300"> before</span> the built-in guessing — but a locked room, and where you've already filed an item on a clean re-import, always win.
            </div>
            {/* Add-rule form */}
            <div className="px-5 py-4 border-b border-[#8b7355]/30 grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                <label className="block text-[10px] uppercase tracking-wide text-stone-500 mb-1">Match by</label>
                <select data-testid={`maprule-type-${target}`} value={newRule.rule_type}
                  onChange={(e) => setNewRule((r) => ({ ...r, rule_type: e.target.value }))}
                  className="w-full bg-black/40 border border-[#8b7355]/50 text-white text-xs rounded px-2 py-1.5 outline-none">
                  <option value="keyword">Keyword in name</option>
                  <option value="vendor">Vendor</option>
                </select>
              </div>
              <div className="col-span-3">
                <label className="block text-[10px] uppercase tracking-wide text-stone-500 mb-1">{newRule.rule_type === 'vendor' ? 'Vendor contains' : 'Name contains'}</label>
                <input data-testid={`maprule-pattern-${target}`} value={newRule.pattern}
                  onChange={(e) => setNewRule((r) => ({ ...r, pattern: e.target.value }))}
                  placeholder={newRule.rule_type === 'vendor' ? 'e.g. Visual Comfort' : 'e.g. sconce'}
                  className="w-full bg-black/40 border border-[#8b7355]/50 text-white text-xs rounded px-2 py-1.5 outline-none" />
              </div>
              <div className="col-span-3">
                <label className="block text-[10px] uppercase tracking-wide text-stone-500 mb-1">Category</label>
                <input data-testid={`maprule-category-${target}`} value={newRule.category}
                  onChange={(e) => setNewRule((r) => ({ ...r, category: e.target.value }))}
                  placeholder="e.g. Lighting"
                  className="w-full bg-black/40 border border-[#8b7355]/50 text-white text-xs rounded px-2 py-1.5 outline-none" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-wide text-stone-500 mb-1">Subcategory</label>
                <input data-testid={`maprule-subcategory-${target}`} value={newRule.subcategory}
                  onChange={(e) => setNewRule((r) => ({ ...r, subcategory: e.target.value }))}
                  placeholder="optional"
                  className="w-full bg-black/40 border border-[#8b7355]/50 text-white text-xs rounded px-2 py-1.5 outline-none" />
              </div>
              <div className="col-span-1">
                <button data-testid={`maprule-add-${target}`} onClick={addRule} disabled={rulesBusy}
                  className="w-full px-2 py-1.5 rounded text-black font-bold text-xs disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>Add</button>
              </div>
            </div>
            {/* Rules list */}
            <div className="overflow-y-auto p-4 space-y-2">
              {rulesBusy && rules.length === 0 && <div className="text-stone-400 text-sm py-6 text-center">Loading…</div>}
              {!rulesBusy && rules.length === 0 && (
                <div className="text-stone-400 text-sm py-6 text-center" data-testid={`toolbar-maprules-empty-${target}`}>
                  No rules yet. Add one above to steer where imports file a vendor or keyword.
                </div>
              )}
              {rules.map((r) => (
                <div key={r.id} data-testid={`maprule-row-${target}`}
                  className="rounded-lg border border-[#8b7355]/40 bg-black/30 px-4 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 text-sm text-white">
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-700/40 text-indigo-200 mr-2">{r.rule_type}</span>
                    “<span className="text-[#D4A574] font-semibold">{r.pattern}</span>” →
                    <span className="text-white font-semibold ml-1">{r.category}</span>
                    {r.subcategory ? <span className="text-stone-400"> › {r.subcategory}</span> : null}
                  </div>
                  <button data-testid={`maprule-delete-${r.id}`} onClick={() => deleteRule(r.id)} disabled={rulesBusy}
                    className="shrink-0 text-xs font-bold rounded bg-rose-800/50 hover:bg-rose-800/70 text-rose-100 px-3 py-1.5 disabled:opacity-50">
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ), document.body)}

      {refileOpen && createPortal((
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100001] p-4"
          data-testid={`toolbar-refile-modal-${target}`} onClick={() => setRefileOpen(false)}>
          <div className="bg-[#1a1a1a] border border-[#8b7355] rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#8b7355]/40 flex items-center justify-between">
              <div className="text-[#D4A574] font-bold text-base">🗂 Re-file Categories — {projectName}</div>
              <button onClick={() => setRefileOpen(false)} data-testid={`toolbar-refile-close-${target}`}
                className="text-stone-400 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="px-5 py-3 border-b border-[#8b7355]/30 text-stone-400 text-xs">
              Sorts every FF&E item into the studio's standard categories by name &amp; vendor. Nothing is deleted, every item keeps its id so your trade sheets stay intact, and you can undo this in one click.
            </div>
            <div className="overflow-y-auto p-4 space-y-3">
              {refileBusy && !refilePreview && !refileResult && (
                <div className="text-stone-400 text-sm py-6 text-center" data-testid={`toolbar-refile-loading-${target}`}>
                  Reading every item — name, vendor &amp; product photo…
                  {refileProgress && refileProgress.total > 0 ? (
                    <><br /><span className="text-[#D4A574] font-bold">Looking at photos: {refileProgress.done}/{refileProgress.total}</span></>
                  ) : (
                    <><br /><span className="text-stone-500 text-xs">Checking names &amp; vendors…</span></>
                  )}
                  <br /><span className="text-stone-500 text-xs">Large projects can take a couple of minutes — this keeps running even if it feels slow.</span>
                </div>
              )}
              {refileResult && (
                <div className="text-center py-6 space-y-3" data-testid={`toolbar-refile-result-${target}`}>
                  <div className="text-emerald-300 text-lg font-bold">✓ Re-filed {refileResult.moved} item{refileResult.moved === 1 ? '' : 's'}</div>
                  <div className="text-stone-400 text-sm">Your grid has been refreshed. If anything looks off, undo it below.</div>
                  <button onClick={undoRefile} disabled={refileBusy} data-testid={`toolbar-refile-undo-${target}`}
                    className="text-xs font-bold rounded bg-amber-700/50 hover:bg-amber-700/70 text-amber-100 px-4 py-2 disabled:opacity-50">
                    {refileBusy ? '⏳…' : '↩ Undo re-file'}
                  </button>
                </div>
              )}
              {!refileResult && refilePreview && !refilePreview.error && (
                <>
                  <div className="text-white text-sm">
                    <span className="text-[#D4A574] font-bold text-lg">{refilePreview.to_move}</span> of {refilePreview.total_items} items will move into the correct bucket.
                  </div>
                  {(refilePreview.ai_classified > 0 || refilePreview.vision_classified > 0) && (
                    <div className="text-stone-400 text-xs" data-testid={`toolbar-refile-aicount-${target}`}>
                      {refilePreview.ai_classified} sorted by AI{refilePreview.vision_classified > 0 ? ` · ${refilePreview.vision_classified} double-checked against the product photo` : ''}
                    </div>
                  )}
                  {refilePreview.to_move === 0 ? (
                    <div className="text-emerald-300 text-sm py-4 text-center" data-testid={`toolbar-refile-clean-${target}`}>Everything is already filed correctly. 🎉</div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-[#8b7355]/40 bg-black/30 p-3">
                        <div className="text-[10px] uppercase tracking-wide text-stone-500 mb-2">Where items will land</div>
                        <div className="space-y-1">
                          {(refilePreview.by_target || []).map((b) => (
                            <div key={b.target} className="flex items-center justify-between text-sm">
                              <span className="text-white">{b.target}</span>
                              <span className="text-stone-400">{b.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-lg border border-[#8b7355]/40 bg-black/30 p-3">
                        <div className="text-[10px] uppercase tracking-wide text-stone-500 mb-2">Sample of what moves</div>
                        <div className="space-y-1.5 max-h-56 overflow-y-auto">
                          {(refilePreview.sample || []).map((s, i) => (
                            <div key={i} className="text-xs text-stone-300">
                              <span className="text-white font-semibold">{s.name || '(unnamed)'}</span>
                              {s.vendor ? <span className="text-stone-500"> · {s.vendor}</span> : null}
                              <span className="text-stone-500"> · {s.room}</span>
                              <div className="text-stone-500">{s.from} <span className="text-teal-300">→ {s.to}</span></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              {refilePreview && refilePreview.error && (
                <div className="text-rose-300 text-sm py-6 text-center">{refilePreview.error}</div>
              )}
            </div>
            {!refileResult && refilePreview && !refilePreview.error && refilePreview.to_move > 0 && (
              <div className="px-5 py-4 border-t border-[#8b7355]/40 flex items-center justify-end gap-2">
                <button onClick={() => setRefileOpen(false)} className="text-xs font-semibold rounded bg-stone-700/50 hover:bg-stone-700/70 text-stone-200 px-4 py-2">Cancel</button>
                <button onClick={doRefile} disabled={refileBusy} data-testid={`toolbar-refile-confirm-${target}`}
                  className="text-xs font-bold rounded text-black px-4 py-2 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>
                  {refileBusy ? '⏳ Re-filing…' : `Re-file ${refilePreview.to_move} items`}
                </button>
              </div>
            )}
          </div>
        </div>
      ), document.body)}

      {historyOpen && createPortal((
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100001] p-4"
          data-testid={`toolbar-history-modal-${target}`} onClick={() => setHistoryOpen(false)}>
          <div className="bg-[#1a1a1a] border border-[#8b7355] rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#8b7355]/40 flex items-center justify-between">
              <div className="text-[#D4A574] font-bold text-base">🕑 Import history — {projectName}</div>
              <button onClick={() => setHistoryOpen(false)} data-testid={`toolbar-history-close-${target}`}
                className="text-stone-400 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {historyBusy && <div className="text-stone-400 text-sm py-6 text-center">Loading…</div>}
              {!historyBusy && history.length === 0 && (
                <div className="text-stone-400 text-sm py-6 text-center" data-testid={`toolbar-history-empty-${target}`}>
                  No imports recorded yet for this project.
                </div>
              )}
              {!historyBusy && history.map((h, idx) => {
                const revertible = h.batch_id && h.batch_id === undoableBatchId;
                const when = h.created_at ? new Date(h.created_at).toLocaleString() : '';
                return (
                  <div key={h.id || idx} data-testid={`toolbar-history-row-${target}`}
                    className="rounded-lg border border-[#8b7355]/40 bg-black/30 px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white text-sm font-semibold truncate">
                        {h.filename || 'Import'} {h.clean_import && <span className="text-amber-300 text-[10px] ml-1">CLEAN</span>}
                      </div>
                      <div className="text-stone-400 text-xs mt-0.5">
                        {when} · {(h.target || 'ffe').toUpperCase()} · {h.products || 0} products · {h.new_items || 0} new / {h.refreshed_items || 0} refreshed · {h.rooms || 0} rooms
                      </div>
                    </div>
                    {revertible ? (
                      <button onClick={undoLastImport} disabled={busy === 'undo'}
                        data-testid={`toolbar-history-revert-${target}`}
                        title="Revert this import (it's the most recent, so it can be rolled back)"
                        className="shrink-0 text-xs font-bold rounded bg-amber-700/50 hover:bg-amber-700/70 text-amber-100 px-3 py-1.5 disabled:opacity-50">
                        {busy === 'undo' ? '⏳…' : '↩ Revert'}
                      </button>
                    ) : (
                      <span className="shrink-0 text-[10px] text-stone-500 px-2" title="Only the most recent import can be reverted.">log only</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ), document.body)}

      {/* UNMATCHED / RESULTS REPORT */}
      {report && createPortal((
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100000] p-4" data-testid={`toolbar-report-modal-${target}`}>
          <div className="rounded-lg w-full max-w-xl max-h-[88vh] flex flex-col" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)', border: '1px solid #8b7355' }}>
            <div className="p-5 border-b border-[#8b7355]/40">
              <h3 className="text-xl text-[#D4C5A9] mb-3">✅ {report.title}</h3>
              {/* One-screen recap stat tiles */}
              <div className="grid grid-cols-3 gap-2" data-testid={`toolbar-report-stats-${target}`}>
                {report.ffe && (
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-300/70">New items</div>
                    <div className="text-lg font-bold text-emerald-300" data-testid={`toolbar-report-stat-new-${target}`}>{report.ffe.items || 0}</div>
                  </div>
                )}
                {report.ffe && (
                  <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-amber-300/70">Refreshed</div>
                    <div className="text-lg font-bold text-amber-300" data-testid={`toolbar-report-stat-refreshed-${target}`}>{report.ffe.updated || 0}</div>
                  </div>
                )}
                {report.ffe && (
                  <div className="rounded-lg bg-sky-500/10 border border-sky-500/20 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-sky-300/70">Rooms merged</div>
                    <div className="text-lg font-bold text-sky-300" data-testid={`toolbar-report-stat-merged-${target}`}>{report.ffe.rooms_merged || 0}</div>
                  </div>
                )}
                {report.finish && (
                  <div className="rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/20 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-fuchsia-300/70">Swatches matched</div>
                    <div className="text-lg font-bold text-fuchsia-300" data-testid={`toolbar-report-stat-swatches-${target}`}>{report.finish.items_updated || 0}</div>
                  </div>
                )}
                {report.finish && (
                  <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-rose-300/70">Unmatched SKUs</div>
                    <div className="text-lg font-bold text-rose-300" data-testid={`toolbar-report-stat-unmatched-${target}`}>{(report.finish.unmatched_skus || []).length}</div>
                  </div>
                )}
                {report.ffe && (report.ffe.duplicates_removed || 0) > 0 && (
                  <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-stone-400">Dupes cleaned</div>
                    <div className="text-lg font-bold text-stone-200" data-testid={`toolbar-report-stat-dupes-${target}`}>{report.ffe.duplicates_removed}</div>
                  </div>
                )}
              </div>
              {report.ffe && target === 'ffe' && <div className="text-[10px] text-stone-500 mt-2">Imported into FF&E and mirrored to the Checklist.</div>}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {(() => {
                const sugg = (report.finish && report.finish.unmatched_suggestions) || [];
                const withSugg = sugg.filter((s) => s.suggestion);
                const unmatchedCount = (report.finish && report.finish.unmatched_skus || []).length;
                if (!report.finish || unmatchedCount === 0) {
                  return <div className="text-center text-emerald-300 py-8 text-sm" data-testid={`toolbar-report-allmatched-${target}`}>🎉 Every tear-sheet SKU matched an item — nothing left unmatched.</div>;
                }
                return (
                  <>
                    {withSugg.length > 0 && (
                      <>
                        <div className="text-[12px] text-[#D4C5A9] font-semibold mb-2">
                          💡 {withSugg.length} tear SKU{withSugg.length === 1 ? '' : 's'} didn't match exactly — here's the closest {label} item. Confirm in one click:
                        </div>
                        <div className="space-y-2 mb-4">
                          {withSugg.map((sg, i) => {
                            const st = confirmedMatches[sg.sku];
                            const s = sg.suggestion;
                            const pct = Math.round((s.score || 0) * 100);
                            return (
                              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10" data-testid={`toolbar-suggest-row-${target}-${i}`}>
                                {sg.swatch_url ? (
                                  <img src={sg.swatch_url} alt="" className="w-10 h-10 object-cover rounded border border-white/15 flex-shrink-0" />
                                ) : (
                                  <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center text-stone-600 text-[9px] flex-shrink-0">no&nbsp;swatch</div>
                                )}
                                <div className="flex-1 min-w-0 text-[11px]">
                                  <div className="text-stone-400">Tear SKU <span className="font-mono text-rose-300">{sg.sku}</span>{sg.color ? ` · ${sg.color}` : ''}</div>
                                  <div className="text-stone-200 truncate flex items-center gap-1" title={s.name}>
                                    <span className="text-[#D4A574]">→</span>
                                    {s.image_url && <img src={s.image_url} alt="" className="w-5 h-5 object-cover rounded flex-shrink-0" />}
                                    <span className="truncate font-medium">{s.name}</span>
                                  </div>
                                  <div className="text-stone-500 text-[10px] truncate">{s.room ? `${s.room} · ` : ''}SKU {s.sku} · <span className="text-emerald-400/80">{pct}% match</span></div>
                                </div>
                                {st === 'done' ? (
                                  <span className="text-emerald-400 text-xs font-semibold px-2 flex-shrink-0" data-testid={`toolbar-suggest-done-${target}-${i}`}>✓ Added</span>
                                ) : (
                                  <button
                                    onClick={() => confirmMatch(sg)}
                                    disabled={st === 'busy' || !sg.swatch_url}
                                    title={!sg.swatch_url ? 'No swatch image was found on the tear sheet for this SKU' : 'Attach this swatch to the suggested item'}
                                    data-testid={`toolbar-suggest-confirm-${target}-${i}`}
                                    className="text-[11px] px-3 py-1.5 rounded font-semibold flex-shrink-0 text-black disabled:opacity-40"
                                    style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}
                                  >
                                    {st === 'busy' ? '…' : 'Confirm'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    {(() => {
                      const noSugg = (report.finish.unmatched_skus || []).filter((sku) => !withSugg.some((w) => w.sku === sku));
                      if (noSugg.length === 0) return null;
                      return (
                        <>
                          <div className="text-[12px] text-rose-300 font-semibold mb-2" data-testid={`toolbar-report-unmatched-count-${target}`}>
                            ⚠ {noSugg.length} SKU(s) had no close match in {label}:
                          </div>
                          <div className="text-[11px] text-stone-300 space-y-1">
                            {noSugg.map((s, i) => (
                              <div key={i} className="px-2 py-1 rounded bg-white/5 font-mono" data-testid={`toolbar-report-unmatched-${target}-${i}`}>{s}</div>
                            ))}
                          </div>
                          <div className="text-[10px] text-stone-500 mt-2">Tip: these weren't found in {label}. Import the FF&E product first (or check the SKU), then re-run the tear-sheet merge.</div>
                        </>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
            <div className="p-4 border-t border-[#8b7355]/40 flex justify-end gap-3">
              <button data-testid={`toolbar-report-close-${target}`} onClick={() => setReport(null)} className="px-4 py-2 rounded text-black font-bold" style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>Done</button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* COMBINED IMPORT + MERGE MODAL */}
      {combine && createPortal((
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100000] p-4" data-testid={`toolbar-combined-modal-${target}`}>
          <div className="rounded-lg w-full max-w-lg flex flex-col" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)', border: '1px solid #8b7355' }}>
            <div className="p-5 border-b border-[#8b7355]/40">
              <h3 className="text-xl text-[#D4C5A9] mb-1">⬆ Import {label} + Tear Sheets — {projectName}</h3>
              <p className="text-stone-400 text-xs">Pick the {label} Schedule PDF and its tear sheet(s), then import & merge in one shot. Each finish/color swatch is pulled into the matching item's finish picture by SKU.</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] text-stone-300 font-semibold mb-1">1) {label} Schedule PDF <span className="text-rose-400">(required)</span></div>
                <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-combined-ffe-${target}`} className="text-[12px] px-3 py-2 rounded cursor-pointer inline-block bg-[#D4A574]/25 text-[#e2c08a] hover:bg-[#D4A574]/45">
                  📄 Choose {label} PDF
                  <input type="file" accept="application/pdf,.pdf" data-testid={`toolbar-combined-ffe-input-${target}`} onChange={(e) => setCombine((c) => ({ ...c, ffeFile: (e.target.files && e.target.files[0]) || null }))} className="hidden" />
                </label>
                {combine.ffeFile && <div className="text-[11px] text-emerald-300 mt-1 truncate">✓ {combine.ffeFile.name}</div>}
              </div>
              <div>
                <div className="text-[11px] text-stone-300 font-semibold mb-1">2) Tear Sheet PDF(s) <span className="text-stone-500">(optional — you can add several)</span></div>
                <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-combined-tear-${target}`} className="text-[12px] px-3 py-2 rounded cursor-pointer inline-block bg-emerald-700/30 text-emerald-200 hover:bg-emerald-700/50">
                  🎨 Choose Tear Sheet(s)
                  <input type="file" accept="application/pdf,.pdf" multiple data-testid={`toolbar-combined-tear-input-${target}`} onChange={(e) => setCombine((c) => ({ ...c, tearFiles: Array.from(e.target.files || []) }))} className="hidden" />
                </label>
                {(combine.tearFiles || []).length > 0 && (
                  <div className="text-[11px] text-emerald-300 mt-1 space-y-0.5">
                    {(combine.tearFiles || []).map((f, i) => <div key={i} className="truncate">✓ {f.name}</div>)}
                  </div>
                )}
              </div>
              <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-combined-hires-${target}`} className="text-[11px] text-stone-400 flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={upgradeCover} data-testid={`toolbar-combined-hires-input-${target}`} onChange={(e) => setUpgradeCover(e.target.checked)} className="w-3 h-3" />
                also upgrade each cover to the tear sheet's hi-res main photo
              </label>
              <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-combined-replace-${target}`} className="text-[11px] text-amber-300/90 flex items-start gap-1.5 cursor-pointer rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1.5">
                <input type="checkbox" checked={replaceExisting} data-testid={`toolbar-combined-replace-input-${target}`} onChange={(e) => setReplaceExisting(e.target.checked)} className="w-3 h-3 mt-0.5 accent-amber-500" />
                <span>🧹 <span className="font-semibold">Clean import</span> — first remove ALL current {label} items{target === 'ffe' ? ' (and Checklist)' : ''}, then load this PDF fresh. Clears stale/wrong rows left by earlier imports. Reversible via “Undo last import”.</span>
              </label>
            </div>
            {combineBusy && combineProgress && (
              <div className="px-4 pb-1 pt-2" data-testid={`toolbar-combined-progress-${target}`}>
                <div className="flex items-center justify-between text-[11px] text-stone-300 mb-1">
                  <span className="truncate pr-2">{combineProgress.stage || 'Working…'}</span>
                  <span className="tabular-nums text-[#D4A574] font-semibold">{Math.round(combineProgress.pct || 0)}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                  <div
                    data-testid={`toolbar-combined-progress-bar-${target}`}
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.max(3, Math.min(100, combineProgress.pct || 0))}%`, background: 'linear-gradient(90deg,#D4A574,#b8863f)' }}
                  />
                </div>
                <div className="text-[10px] text-stone-500 mt-1">Big schedules can take a minute or two — you can leave this open.</div>
              </div>
            )}
            <div className="p-4 border-t border-[#8b7355]/40 flex justify-end gap-3">
              <button data-testid={`toolbar-combined-cancel-${target}`} onClick={() => setCombine(null)} disabled={combineBusy} className="px-4 py-2 rounded text-stone-300 border border-white/10 hover:bg-white/5 disabled:opacity-50">Cancel</button>
              <button data-testid={`toolbar-combined-run-${target}`} onClick={runCombined} disabled={combineBusy || !combine.ffeFile} className="px-4 py-2 rounded text-black font-bold disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>
                {combineBusy ? 'Importing & merging…' : 'Import & Merge'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* IMPORT PREVIEW MODAL */}
      {preview && createPortal((
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100000] p-4" data-testid={`toolbar-preview-modal-${target}`}>
          <div className="rounded-lg w-full max-w-3xl max-h-[85vh] flex flex-col" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)', border: '1px solid #8b7355' }}>
            <div className="p-5 border-b border-[#8b7355]/40">
              <h3 className="text-xl text-[#D4C5A9] mb-1">Import preview — {projectName} → {label}</h3>
              <p className="text-stone-400 text-xs">
                <span className="text-emerald-300 font-semibold">{preview.data.will_add}</span> new · <span className="text-amber-300 font-semibold">{preview.data.will_update}</span> refreshed · {preview.data.total} total. Nothing is saved until you confirm.
              </p>
            </div>
            <div className="flex-1 overflow-auto p-3">
              <div className="text-[11px] text-stone-300">
                <div className="flex items-center gap-2 px-2 py-1 text-stone-500 sticky top-0 bg-[#141422] font-semibold">
                  <div className="w-14 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">Item</div>
                  <div className="w-20 flex-shrink-0">Room</div>
                  <div className="w-20 flex-shrink-0">Vendor</div>
                  <div className="w-16 flex-shrink-0">SKU</div>
                  <div className="w-16 flex-shrink-0 text-right">Price</div>
                </div>
                {(preview.data.items || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 border-t border-white/5" data-testid={`toolbar-preview-row-${target}-${i}`}>
                    <div className="w-14 flex-shrink-0">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] ${it.existing ? 'bg-amber-600/30 text-amber-200' : 'bg-emerald-600/30 text-emerald-200'}`}>{it.existing ? 'REFRESH' : 'NEW'}</span>
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-2" title={it.name}>
                      {it.thumb ? (
                        <img src={it.thumb} alt="" className="w-9 h-9 object-cover rounded border border-white/10 flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center text-stone-600 text-[9px] flex-shrink-0">—</div>
                      )}
                      <span className="truncate">{it.name}</span>
                    </div>
                    <div className="w-20 flex-shrink-0 text-stone-400 truncate" title={it.room}>{it.room}</div>
                    <div className="w-20 flex-shrink-0 text-stone-400 truncate" title={it.vendor}>{it.vendor}</div>
                    <div className="w-16 flex-shrink-0 text-stone-500 truncate" title={it.sku}>{it.sku}</div>
                    <div className="w-16 flex-shrink-0 text-right">{it.price ? `$${it.price.toLocaleString()}` : ''}</div>
                  </div>
                ))}
              </div>
            </div>
            {busy === 'commit' && combineProgress && (
              <div className="px-4 pb-1 pt-2" data-testid={`toolbar-preview-progress-${target}`}>
                <div className="flex items-center justify-between text-[11px] text-stone-300 mb-1">
                  <span className="truncate pr-2">{combineProgress.stage || 'Working…'}</span>
                  <span className="tabular-nums text-[#D4A574] font-semibold">{Math.round(combineProgress.pct || 0)}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                  <div
                    data-testid={`toolbar-preview-progress-bar-${target}`}
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.max(3, Math.min(100, combineProgress.pct || 0))}%`, background: 'linear-gradient(90deg,#D4A574,#b8863f)' }}
                  />
                </div>
                <div className="text-[10px] text-stone-500 mt-1">Big schedules can take a minute or two — you can leave this open.</div>
              </div>
            )}
            <div className="p-4 border-t border-[#8b7355]/40 flex items-center justify-between gap-3">
              <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-preview-replace-${target}`} className="text-[11px] text-amber-300/90 flex items-center gap-1.5 cursor-pointer" title={`Clean import: first remove ALL current ${label} items${target === 'ffe' ? ' (and Checklist)' : ''}, then load this PDF fresh — clears stale/wrong rows from earlier imports. Reversible via Undo last import.`}>
                <input type="checkbox" checked={replaceExisting} data-testid={`toolbar-preview-replace-input-${target}`} onChange={(e) => setReplaceExisting(e.target.checked)} className="w-3 h-3 accent-amber-500" />
                🧹 clean import (replace current {label} items)
              </label>
              <div className="flex gap-3">
                <button data-testid={`toolbar-preview-cancel-${target}`} onClick={() => setPreview(null)} className="px-4 py-2 rounded text-stone-300 border border-white/10 hover:bg-white/5">Cancel</button>
                <button data-testid={`toolbar-preview-confirm-${target}`} onClick={confirmImport} disabled={busy === 'commit'} className="px-4 py-2 rounded text-black font-bold disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>
                  {busy === 'commit' ? 'Importing…' : (replaceExisting ? `Clean import ${preview.data.total} items` : `Import ${preview.data.total} items`)}
                </button>
              </div>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* FIX MISSING FINISHES MODAL */}
      {missing && createPortal((
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100000] p-4" data-testid={`toolbar-missing-modal-${target}`}>
          <div className="rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)', border: '1px solid #8b7355' }}>
            <div className="p-5 border-b border-[#8b7355]/40">
              <h3 className="text-xl text-[#D4C5A9] mb-1">🩹 Fix missing finishes — {projectName} ({label})</h3>
              <p className="text-stone-400 text-xs">
                {(missing.items || []).length === 0
                  ? `Every ${label} item already has a finish swatch. 🎉`
                  : <><span className="text-rose-300 font-semibold">{missing.items.length}</span> {label} item{missing.items.length === 1 ? '' : 's'} still missing a finish swatch. Upload a flat material/color swatch for each (never the furniture photo).</>}
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-auto-match-${target}`} title="Upload a Houzz tear-sheet PDF — we auto-match swatches by SKU, then this list shrinks to just what still needs a manual pick" className={`text-[11px] px-3 py-1.5 rounded cursor-pointer inline-flex items-center gap-1 font-semibold ${missingBusy ? 'bg-indigo-700/30 text-stone-400' : 'bg-indigo-600/60 text-indigo-50 hover:bg-indigo-600/80'}`}>
                  {missingBusy ? '⏳ Matching…' : '🎨 Auto-match from tear sheet'}
                  <input type="file" accept="application/pdf,.pdf" data-testid={`toolbar-auto-match-input-${target}`} disabled={missingBusy} onChange={autoMatchFinishes} className="hidden" />
                </label>
                <span className="text-[10px] text-stone-500">Runs the SKU swatch-matcher, then leaves only the items you must pick by hand.</span>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-3">
              {(missing.items || []).length === 0 ? (
                <div className="text-center text-stone-500 py-10 text-sm" data-testid={`toolbar-missing-empty-${target}`}>All set — nothing to fix.</div>
              ) : (
                <div className="text-[11px] text-stone-300">
                  <div className="flex items-center gap-2 px-2 py-1 text-stone-500 sticky top-0 bg-[#141422] font-semibold">
                    <div className="flex-1 min-w-0">Item</div>
                    <div className="w-20 flex-shrink-0">Room</div>
                    <div className="w-16 flex-shrink-0">SKU</div>
                    <div className="w-20 flex-shrink-0">Vendor</div>
                    <div className="w-24 flex-shrink-0 text-right">Finish</div>
                  </div>
                  {(missing.items || []).map((it, i) => (
                    <div key={it.id} className="flex items-center gap-2 px-2 py-1.5 border-t border-white/5" data-testid={`toolbar-missing-row-${target}-${i}`}>
                      <div className="flex-1 min-w-0 flex items-center gap-2" title={it.name}>
                        {it.image_url ? (
                          <img src={it.image_url} alt="" className="w-9 h-9 object-cover rounded border border-white/10 flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center text-stone-600 text-[9px] flex-shrink-0">—</div>
                        )}
                        <span className="truncate">{it.name}</span>
                      </div>
                      <div className="w-20 flex-shrink-0 text-stone-400 truncate" title={it.room}>{it.room}</div>
                      <div className="w-16 flex-shrink-0 text-stone-500 truncate" title={it.sku}>{it.sku}</div>
                      <div className="w-20 flex-shrink-0 text-stone-400 truncate" title={it.vendor}>{it.vendor}</div>
                      <div className="w-24 flex-shrink-0 text-right">
                        <label onClick={(e) => e.stopPropagation()} data-testid={`toolbar-missing-upload-${target}-${it.id}`} className={`text-[10px] px-2 py-1 rounded cursor-pointer inline-block whitespace-nowrap ${missingBusy ? 'bg-emerald-700/30 text-stone-400' : 'bg-emerald-700/40 text-emerald-100 hover:bg-emerald-700/60'}`}>
                          ＋ Upload
                          <input type="file" accept="image/*" data-testid={`toolbar-missing-input-${target}-${it.id}`} disabled={missingBusy} onChange={(e) => uploadFinishForItem(it.id, e)} className="hidden" />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[#8b7355]/40 flex justify-end gap-3">
              <button data-testid={`toolbar-missing-close-${target}`} onClick={() => setMissing(null)} className="px-4 py-2 rounded text-black font-bold" style={{ background: 'linear-gradient(135deg,#D4A574,#b8863f)' }}>Done</button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export default HouzzToolbar;
